'use client';

import React, { createContext, useContext, useRef, useCallback } from 'react';
import type { Map as LeafletMap } from 'leaflet';

interface MapControlsContextValue {
  mapRef: React.MutableRefObject<LeafletMap | null>;
  zoomIn: () => void;
  zoomOut: () => void;
  recenter: () => void;
  toggleLayer: () => void;
  flyTo: (lat: number, lng: number, zoom?: number) => void;
}

const MapControlsContext = createContext<MapControlsContextValue>({
  mapRef: { current: null },
  zoomIn: () => {},
  zoomOut: () => {},
  recenter: () => {},
  toggleLayer: () => {},
  flyTo: () => {},
});

// West Delhi center coordinates (fallback)
const CENTER_LAT = 28.6200;
const CENTER_LNG = 77.0400;
const DEFAULT_ZOOM = 15;

export function MapControlsProvider({ children }: { children: React.ReactNode }) {
  const mapRef = useRef<LeafletMap | null>(null);

  const zoomIn = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomIn(1);
    }
  }, []);

  const zoomOut = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomOut(1);
    }
  }, []);

  const recenter = useCallback(() => {
    if (!mapRef.current) return;

    if (!navigator.geolocation) {
      // Geolocation not supported — fall back to West Delhi center
      mapRef.current.flyTo([CENTER_LAT, CENTER_LNG], DEFAULT_ZOOM, {
        animate: true,
        duration: 0.8,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        mapRef.current?.flyTo([latitude, longitude], DEFAULT_ZOOM, {
          animate: true,
          duration: 0.8,
        });
      },
      (_error) => {
        // Permission denied or unavailable — fall back to West Delhi center
        mapRef.current?.flyTo([CENTER_LAT, CENTER_LNG], DEFAULT_ZOOM, {
          animate: true,
          duration: 0.8,
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  }, []);

  const toggleLayer = useCallback(() => {
    // Backend integration point: switch between satellite and street tile layers
  }, []);

  const flyTo = useCallback((lat: number, lng: number, zoom = 15) => {
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lng], zoom, { animate: true, duration: 1.0 });
    }
  }, []);

  return (
    <MapControlsContext.Provider value={{ mapRef, zoomIn, zoomOut, recenter, toggleLayer, flyTo }}>
      {children}
    </MapControlsContext.Provider>
  );
}

export function useMapControlsContext() {
  return useContext(MapControlsContext);
}